/**
 * dcent-app-demos.js — 포트폴리오 데모 중 **움직이는 것**의 재생 제어.
 *
 * 지금은 DeFi 화면 하나뿐이다(Receive·NFT·거래내역은 정지 화면이라 JS 가 없다).
 * 마크업은 `snippets/dcent-app-defi-demo.liquid`, 외형은 `dcent-app-demos.css`.
 * 원본: `20260827 homepage_전달용/src/components/DefiPositionsDemo.tsx` 의 useEffect + CountUp.
 *
 * 금액 4자리가 1.4s ease-out 으로 올라간다. 보일 때 재생하고 벗어나면 0 으로 되돌려
 * 재진입 때 다시 세도록 한다(원본과 같은 동작).
 *
 * ⚠️ **reduced-motion 이면 카운팅 없이 완성 금액을 그린다.** 그냥 멈추면 $0.00 이 박힌
 * 화면이 남아 "포지션이 한눈에 보인다"는 요지가 사라진다.
 *
 * 완성 문자열은 마크업이 이미 갖고 있다 — 첫 렌더 텍스트를 기억해 두었다가 그대로 되돌린다.
 * 여기서 다시 포맷하면 마크업과 규칙이 갈려 마지막 프레임에 표기가 튄다.
 *
 * 안전 규약: 대상 없으면 조용히 종료 · 멱등 설치 · 보일 때만 재생 · 이탈 시 리셋.
 *
 * 양 샵 byte-identical — assets/dcent-* 는 parity 게이트 분모다.
 */

if (!window.__dcentAppDemos) {
  window.__dcentAppDemos = true;

  const DUR = 1400;

  const setup = (root) => {
    const counters = Array.from(root.querySelectorAll("[data-count-to]")).map(
      (el) => ({
        el,
        to: Number(el.dataset.countTo),
        dec: Number(el.dataset.countDec) || 0,
        done: el.textContent,
      }),
    );
    if (!counters.length) return;

    const fmt = (c, v) =>
      v.toLocaleString("en-US", {
        minimumFractionDigits: c.dec,
        maximumFractionDigits: c.dec,
      });

    if (
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ) {
      return; // 마크업이 이미 완성값이다 — 손대지 않는 것이 곧 완성 화면
    }

    let raf = 0;
    const play = (on) => {
      cancelAnimationFrame(raf);
      if (!on) {
        counters.forEach((c) => {
          c.el.textContent = fmt(c, 0);
        });
        return;
      }
      const t0 = performance.now();
      const tick = (t) => {
        const p = Math.min(1, (t - t0) / DUR);
        const eased = 1 - Math.pow(1 - p, 3);
        counters.forEach((c) => {
          c.el.textContent = p < 1 ? fmt(c, c.to * eased) : c.done;
        });
        if (p < 1) raf = requestAnimationFrame(tick);
      };
      raf = requestAnimationFrame(tick);
    };

    play(false);

    if (!("IntersectionObserver" in window)) {
      play(true);
      return;
    }
    // threshold 0.4 — 이 화면은 카드 안에 통째로 들어가 있어 원본 값 그대로 넘긴다
    const io = new IntersectionObserver(([e]) => play(e.isIntersecting), {
      threshold: 0.4,
    });
    io.observe(root);
  };

  const init = () => {
    document.querySelectorAll("[data-app-defi]").forEach((root) => {
      if (root.dataset.appDemoReady === "1") return; // 멱등
      root.dataset.appDemoReady = "1";
      setup(root);
    });
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init, { once: true });
  } else {
    init();
  }
}
